import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Crop, RotateCw, Trash2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";

function draw(canvas, p) {
  const img = new Image();
  img.onload = () => {
    const turned = p.rotate % 180 !== 0;
    const W = turned ? img.height : img.width, H = turned ? img.width : img.height;
    const tmp = document.createElement("canvas");
    tmp.width = W; tmp.height = H;
    const t = tmp.getContext("2d");
    t.translate(W / 2, H / 2); t.rotate((p.rotate * Math.PI) / 180); t.drawImage(img, -img.width / 2, -img.height / 2);
    const c = p.crop;
    const sx = W * c.left, sy = H * c.top, sw = W * (1 - c.left - c.right), sh = H * (1 - c.top - c.bottom);
    const scale = 220 / Math.max(sw, sh);
    canvas.width = Math.round(sw * scale); canvas.height = Math.round(sh * scale);
    const ctx = canvas.getContext("2d");
    ctx.filter = p.scan ? "grayscale(1) brightness(1.1) contrast(1.4)" : "none";
    ctx.drawImage(tmp, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  };
  img.src = p.preview;
}

const SIDES = [["top", "Top"], ["bottom", "Bottom"], ["left", "Left"], ["right", "Right"]];
const Btn = ({ onClick, disabled, label, testId, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testId} className="rounded-md border p-1.5 text-navy hover:bg-navy/5 disabled:opacity-30">{children}</button>
);

export const PhotoPageCard = ({ page, index, total, onChange, onMove, onRemove }) => {
  const ref = useRef(null);
  const [cropping, setCropping] = useState(false);
  useEffect(() => { if (ref.current) draw(ref.current, page); }, [page]);
  const setCrop = (side, v) => onChange({ crop: { ...page.crop, [side]: v / 100 } });
  return (
    <li data-testid={`photo-page-${index}`} className="flex flex-col gap-3 rounded-lg border bg-white p-3 sm:flex-row">
      <div className="grid h-[230px] w-full shrink-0 place-items-center rounded bg-slate-100 sm:w-[230px]"><canvas ref={ref} data-testid={`photo-page-preview-${index}`} className="max-h-[220px] max-w-full shadow" /></div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-auto text-sm font-medium text-navy">Page {index + 1}</span>
          <Btn onClick={() => onMove(-1)} disabled={index === 0} label="Move up" testId={`photo-page-up-${index}`}><ArrowUp className="h-4 w-4" /></Btn>
          <Btn onClick={() => onMove(1)} disabled={index === total - 1} label="Move down" testId={`photo-page-down-${index}`}><ArrowDown className="h-4 w-4" /></Btn>
          <Btn onClick={() => onChange({ rotate: (page.rotate + 90) % 360 })} label="Rotate" testId={`photo-page-rotate-${index}`}><RotateCw className="h-4 w-4" /></Btn>
          <Btn onClick={() => setCropping((c) => !c)} label="Crop" testId={`photo-page-crop-${index}`}><Crop className="h-4 w-4" /></Btn>
          <Btn onClick={onRemove} label="Remove page" testId={`photo-page-remove-${index}`}><Trash2 className="h-4 w-4 text-red-700" /></Btn>
        </div>
        <label className="flex items-center justify-between gap-3 text-sm text-slate-700">Scan look (brighter, black and white)
          <Switch checked={page.scan} onCheckedChange={(v) => onChange({ scan: v })} data-testid={`photo-page-scan-${index}`} /></label>
        {cropping && (
          <div className="space-y-2 rounded-md bg-slate-50 p-3" data-testid={`photo-page-crop-panel-${index}`}>
            {SIDES.map(([side, label]) => (
              <div key={side} className="flex items-center gap-3 text-xs text-slate-600"><span className="w-14">{label}</span>
                <Slider value={[Math.round(page.crop[side] * 100)]} max={45} step={1} onValueChange={([v]) => setCrop(side, v)} data-testid={`photo-page-crop-${side}-${index}`} /></div>
            ))}
          </div>
        )}
      </div>
    </li>
  );
};
